import { PublicKey } from '@solana/web3.js';
import bs58 from 'bs58';
import wretch from 'wretch';
import type { Wretch } from 'wretch';
import { SigninMessage } from '../../utils/sign-message';
import { SugarMoneyLoginOptions, SugarMoneyLoginResponseType } from 'types/auth';

function parseCookies(cookieHeader: string | null): { [key: string]: string } {
  const cookies: { [key: string]: string } = {};
  if (!cookieHeader) return cookies;

  cookieHeader.split(',').forEach((cookie) => {
    const parts = cookie.split(';')[0].trim().split('=');
    if (parts.length === 2) {
      cookies[parts[0].trim()] = parts[1].trim();
    }
  });

  return cookies;
}

function mergeCookies(
  existingCookies: { [key: string]: string },
  setCookieHeader: string | null,
): { [key: string]: string } {
  if (!setCookieHeader) return existingCookies;

  const newCookies = parseCookies(setCookieHeader);
  return { ...existingCookies, ...newCookies };
}

function stringifyCookies(cookies: { [key: string]: string }): string {
  return Object.entries(cookies)
    .map(([key, value]) => `${key}=${value}`)
    .join('; ');
}

async function getCsrfToken({
  apiInstance,
}: {
  apiInstance: Wretch;
}): Promise<{ csrfToken: string; cookies: { [key: string]: string } }> {
  const response = await apiInstance.url('/csrf').get().res();
  const newCookies = mergeCookies({}, response.headers.get('set-cookie'));
  const data = (await response.json()) as { csrfToken: string };

  return {
    csrfToken: data.csrfToken,
    cookies: newCookies,
  };
}

export const login = async ({
  signMessageFn,
  baseUrl,
  publicKey,
  apiInstance,
}: SugarMoneyLoginOptions): Promise<SugarMoneyLoginResponseType> => {
  try {
    const { csrfToken, cookies } = await getCsrfToken({ apiInstance });
    if (!csrfToken) throw new Error('No CSRF token available');

    const message = new SigninMessage({
      domain: new URL(baseUrl).host,
      publicKey: publicKey.toBase58(),
      statement: 'Sign this message to sign in to the app.',
      nonce: csrfToken,
    });

    const messageBytes = new TextEncoder().encode(message.prepare());
    const signature = await signMessageFn(messageBytes);
    const serializedSignature = bs58.encode(signature);

    const formData = new URLSearchParams({
      message: JSON.stringify({ message }),
      redirect: 'false',
      signature: serializedSignature,
      csrfToken,
      json: 'true',
    });

    const response = await apiInstance
      .url('/callback/credentials')
      .headers({
        'Content-Type': 'application/x-www-form-urlencoded',
        Cookie: stringifyCookies(cookies),
      })
      .body(formData.toString())
      .post()
      .res();

    const setCookieHeader = response.headers.get('set-cookie');
    if (!setCookieHeader) {
      throw new Error('No cookies received from server');
    }

    const csrfCookie = setCookieHeader
      .split(',')
      .find((cookie) => cookie.includes('next-auth.session-token'));

    if (!csrfCookie) {
      throw new Error('CSRF token cookie not found');
    }

    const token = csrfCookie.split(';')[0].split('=')[1];
    return token;
  } catch (error) {
    throw error;
  }
};

export const Auth = {
  login,
};
